// useBrokerDocs - one-time read of a company's broker subcollection, mirroring
// the legacy Team page's `fetchBrokerDocs`. Shared by the Invitations tab
// (broker select + linked brokers) and the Assignments tab (source / target
// selects + name resolution). No new Firestore access beyond the legacy page.

import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../../lib/firebase';

/** One explicitly linked pipeline broker id, as stored on the broker document. */
export interface BrokerIdentityGroupMemberDoc {
  brokerId: string;
  observedName?: string;
  reason?: string;
}

export interface BrokerDoc {
  id: string;
  currentName: string;
  currentOfficeId?: string;
  status?: string;
  mergedInto?: string;
  /**
   * Explicit identity grouping. Read-only here: the ONLY write path is the
   * audited `setBrokerIdentityGroup` callable, and `firestore.rules` refuses a
   * client write to the field.
   */
  identityGroup?: {
    members: BrokerIdentityGroupMemberDoc[];
    memberIds: string[];
    revision?: number;
  };
  /**
   * Explicit primary lending entity. Absent means "not configured", which the
   * Broker Report states out loud rather than guessing around.
   */
  primaryEntityCode?: string;
}

export interface BrokerDocsResult {
  brokerDocs: BrokerDoc[];
  // brokerId -> currentName, for label lookups.
  brokerNameById: Map<string, string>;
  getBrokerName: (brokerDocId: string) => string;
  /** Re-read the subcollection, for a screen that writes to it. */
  refresh: () => void;
}

export function useBrokerDocs(companyId: string | undefined): BrokerDocsResult {
  const [brokerDocs, setBrokerDocs] = useState<BrokerDoc[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!companyId) {
      setBrokerDocs([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, `companies/${companyId}/brokers`));
        if (cancelled) return;
        const docs = snap.docs.map((docSnap) => {
          const data = docSnap.data() as Record<string, unknown>;
          return {
            id: docSnap.id,
            currentName: (data.currentName as string) || docSnap.id,
            currentOfficeId: data.currentOfficeId as string | undefined,
            status: data.status as string | undefined,
            mergedInto: data.mergedInto as string | undefined,
            identityGroup: data.identityGroup as BrokerDoc['identityGroup'],
            primaryEntityCode:
              typeof data.primaryEntityCode === 'string' && data.primaryEntityCode
                ? data.primaryEntityCode
                : undefined,
          } as BrokerDoc;
        });
        setBrokerDocs(docs);
      } catch (err) {
        // Never silent: a rule-gated read that fails must be visible, or the
        // screen reads as "no brokers configured" when it is really "denied".
        console.error('[TeamManagementV2] Error fetching broker docs:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, refreshKey]);

  const brokerNameById = useMemo(() => {
    const map = new Map<string, string>();
    brokerDocs.forEach((b) => map.set(b.id, b.currentName));
    return map;
  }, [brokerDocs]);

  const getBrokerName = useMemo(
    () => (brokerDocId: string): string => brokerNameById.get(brokerDocId) || brokerDocId,
    [brokerNameById],
  );

  const refresh = useMemo(() => () => setRefreshKey((k) => k + 1), []);

  return { brokerDocs, brokerNameById, getBrokerName, refresh };
}
